import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import { alpha } from '@mui/material/styles';

function fieldName(field) {
  return field?.name || field?.key || '';
}

function fieldLabel(field) {
  return field?.label || field?.display_name || fieldName(field);
}

function optionValue(option) {
  if (option && typeof option === 'object') {
    return String(option.value ?? option.key ?? option.label ?? '').trim();
  }
  return String(option ?? '').trim();
}

function optionLabel(option) {
  if (option && typeof option === 'object') {
    return String(option.label ?? option.value ?? option.key ?? '').trim();
  }
  return String(option ?? '').trim();
}

function normalizedOptions(field) {
  return (Array.isArray(field?.options) ? field.options : [])
    .map((option) => ({ value: optionValue(option), label: optionLabel(option) }))
    .filter((option) => option.value);
}

function textValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return JSON.stringify(value, null, 2);
  }
  return value ?? '';
}

export default function SchemaBodyFields({
  fields,
  values = {},
  fileValues = {},
  errors = {},
  disabled = false,
  onChange,
  onFileChange,
}) {
  const renderField = (field) => {
    const name = fieldName(field);
    const label = fieldLabel(field);
    const type = String(field?.type || 'text').toLowerCase();
    const fieldError = errors[name];
    const value = values[name] ?? '';
    const description = field?.description || field?.helper_text || ' ';
    const commonProps = {
      key: name,
      label,
      fullWidth: true,
      required: Boolean(field?.required),
      disabled,
      error: Boolean(fieldError),
      variant: 'outlined',
      sx: { mb: 2 },
      value,
      onChange: (event) => onChange?.(field, event.target.value),
      helperText: fieldError || description,
    };

    if (['textarea', 'key_value_pairs', 'structured_data', 'physical_item', 'reference'].includes(type)) {
      return <TextField {...commonProps} value={textValue(value)} multiline rows={type === 'key_value_pairs' ? 3 : 4} />;
    }
    if (type === 'number') {
      return <TextField {...commonProps} type="number" inputProps={{ step: 'any', min: field.min, max: field.max }} />;
    }
    if (type === 'date') {
      return <TextField {...commonProps} type="date" InputLabelProps={{ shrink: true }} />;
    }
    if (type === 'datetime') {
      return <TextField {...commonProps} type="datetime-local" InputLabelProps={{ shrink: true }} />;
    }
    if (type === 'email') {
      return <TextField {...commonProps} type="email" />;
    }
    if (type === 'url') {
      return <TextField {...commonProps} type="url" />;
    }
    if (type === 'boolean') {
      return (
        <Box
          key={name}
          sx={{
            mb: 2,
            px: 2,
            py: 1.5,
            borderRadius: 1,
            border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.58),
          }}
        >
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(values[name])}
                disabled={disabled}
                onChange={(event) => onChange?.(field, event.target.checked)}
              />
            )}
            required={Boolean(field?.required)}
            label={label}
          />
          <Typography variant="caption" color={fieldError ? 'error' : 'text.secondary'} sx={{ display: 'block' }}>
            {fieldError || description}
          </Typography>
        </Box>
      );
    }
    if (type === 'select' || type === 'selection') {
      return (
        <TextField {...commonProps} select>
          {normalizedOptions(field).map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      );
    }
    if (type === 'multiselect' || type === 'tags') {
      const selected = Array.isArray(values[name]) ? values[name] : [];
      return (
        <FormControl
          key={name}
          fullWidth
          required={Boolean(field?.required)}
          disabled={disabled}
          error={Boolean(fieldError)}
          sx={{ mb: 2 }}
        >
          <InputLabel id={`label-${name}`}>{label}</InputLabel>
          <Select
            labelId={`label-${name}`}
            multiple
            value={selected}
            onChange={(event) => onChange?.(field, event.target.value)}
            input={<OutlinedInput label={label} />}
            renderValue={(selectedValues) => selectedValues.join(', ')}
          >
            {normalizedOptions(field).map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={selected.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{fieldError || description}</FormHelperText>
        </FormControl>
      );
    }
    if (type === 'file') {
      const selectedFile = fileValues[name] || values[name];
      const selectedLabel = Array.isArray(selectedFile)
        ? selectedFile.map((file) => file?.name || file).filter(Boolean).join(', ') || label
        : selectedFile?.name || selectedFile || label;
      return (
        <Box key={name} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            fullWidth
            disabled={disabled}
            startIcon={<CloudUploadRoundedIcon />}
            sx={{
              py: 1.6,
              justifyContent: 'flex-start',
              borderRadius: 1,
              borderStyle: 'dashed',
            }}
          >
            {selectedLabel}
            <input
              type="file"
              hidden
              multiple={Boolean(field.multiple)}
              accept={field.accept || field.accepted_file_types || '*'}
              onChange={(event) => {
                const files = Array.from(event.target.files || []);
                if (files.length) {
                  onFileChange?.(field, field.multiple ? files : files[0]);
                }
              }}
            />
          </Button>
          <Typography variant="caption" color={fieldError ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
            {fieldError || description}
          </Typography>
        </Box>
      );
    }
    if (type === 'range') {
      return (
        <Box key={name} sx={{ mb: 4, px: 1 }}>
          <Typography gutterBottom sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Slider
            value={values[name] || 0}
            disabled={disabled}
            onChange={(_event, nextValue) => onChange?.(field, nextValue)}
            step={1}
            min={field.min || 0}
            max={field.max || 10000}
            valueLabelDisplay="auto"
          />
          <Typography variant="caption" color={fieldError ? 'error' : 'text.secondary'}>
            {fieldError || description}
          </Typography>
        </Box>
      );
    }
    if (type === 'instruction') {
      return (
        <Box key={name} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {field?.description || field?.helper_text || ''}
          </Typography>
        </Box>
      );
    }
    return <TextField {...commonProps} type="text" />;
  };

  return (
    <Stack spacing={0}>
      {(Array.isArray(fields) ? fields : [])
        .filter((field) => field && typeof field === 'object' && fieldName(field))
        .map((field) => renderField(field))}
    </Stack>
  );
}
